import { HStack } from "@chakra-ui/react";
import { DataTable, LoadingOverlay } from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import type {
  ContractsWhereColumn,
  OrdersWhereColumn,
  OtherDocumentsWhereColumn,
} from "@src/__generated__/graphql";
import {
  FiltersPanel,
  FiltersPanelRoot,
} from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { withCan } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FunctionComponent, type ReactElement, useContext } from "react";
import { AddDocumentBtn, DocumentsTables, TabTableNavigation } from "./Common";
import { useDocumentData } from "./PageDocuments";
import {
  DocumentsCtx,
  type ExternalLinkColumns,
  QuickFilters,
} from "./StoreDocuments";

type TableModuleDocumentsProps = {
  navigation?: ReactElement;
  bordered?: boolean;
};
export const TableModuleDocuments: FunctionComponent<
  React.PropsWithChildren<TableModuleDocumentsProps>
> = withCan(
  { do: "contract_read", fail: false },
  observer(function TableModuleDocuments({ navigation, bordered }) {
    const store = useContext(DocumentsCtx);
    const { loading } = store;

    useDocumentData(store);

    return (
      <DocumentsCtx.Provider value={store}>
        <DataTable bordered={bordered}>
          {navigation ? (
            <>
              <HStack
                justifyContent="space-between"
                px="4"
                borderBottom="1px"
                borderBottomColor="gray.200"
              >
                {navigation}

                <AddDocumentBtn />
              </HStack>

              <DataTable.Filter>
                <FiltersPanelRoot<
                  | ContractsWhereColumn
                  | OrdersWhereColumn
                  | OtherDocumentsWhereColumn
                  | ExternalLinkColumns
                >
                  filters={store.where}
                >
                  <HStack w="full" spacing="2">
                    <TabTableNavigation store={store} variant="menu" />

                    <FiltersPanelSearch
                      w="full"
                      inputWidth="full"
                      onSearch={(val) => {
                        store.searchTerm = val;
                        store.where.onChange?.();
                      }}
                    />

                    {store.where.visibleFilters.length > 0 && (
                      <FiltersPanelFilters />
                    )}
                  </HStack>
                </FiltersPanelRoot>
              </DataTable.Filter>
            </>
          ) : (
            <>
              <DataTable.NavBar>
                <HStack justify="space-between" w="full">
                  <TabTableNavigation store={store} variant="tabs" />
                  <AddDocumentBtn />
                </HStack>
              </DataTable.NavBar>

              <DataTable.Filter>
                <FiltersPanel<
                  | ContractsWhereColumn
                  | OrdersWhereColumn
                  | OtherDocumentsWhereColumn
                  | ExternalLinkColumns
                >
                  filters={store.where}
                  onSearch={(val) => {
                    store.searchTerm = val;
                    store.where.onChange?.();
                  }}
                />
              </DataTable.Filter>
            </>
          )}

          <LoadingOverlay isLoading={loading} position="absolute">
            <DocumentsTables store={store} />
          </LoadingOverlay>

          {store.activeQuickFilter !== QuickFilters.ExternalLinks && (
            <Pagination store={store.pagination} />
          )}
        </DataTable>
      </DocumentsCtx.Provider>
    );
  }),
);
